import { isCancel } from 'axios';
import { ref } from 'vue';

interface UseLatestCallOptions<R> {
  onSuccess?: (result: R) => void;
  onError?: (error: unknown) => void;
}

export function useLatestCall<T extends any[], R>(
  fn: (signal: AbortSignal, ...args: T) => Promise<R>,
  options: UseLatestCallOptions<R>,
) {
  let ctrl: AbortController | null = null;

  const processing = ref(false);
  const lastCallCompletedAt = ref<number | null>(null);

  function abort() {
    ctrl?.abort();
    ctrl = null;
    processing.value = false;
  }

  async function call(...args: T) {
    processing.value = true;

    ctrl?.abort();

    const currentCtrl = new AbortController();
    ctrl = currentCtrl;

    try {
      const result = await fn(currentCtrl.signal, ...args);

      if (currentCtrl !== ctrl) {
        return;
      }

      lastCallCompletedAt.value = Date.now();
      options?.onSuccess?.(result);
    } catch (error: any) {
      if (isCancel(error) || currentCtrl !== ctrl) {
        return;
      }

      options?.onError?.(error);

      throw error;
    } finally {
      if (currentCtrl === ctrl) {
        ctrl = null;
        processing.value = false;
      }
    }
  }

  return {
    processing,
    call,
    abort,
    lastCallCompletedAt,
  };
}
